import React, { useState } from "react";
import httpClient from "../../../utils/httpClient";

type Superintendent = {
  emailAddress: string;
  name: string;
  builderId: string;
  builderNumber: string;
};

type Employee = {
  emailAddress: string;
  employeeNumber: string;
  employeeFullName: string;
};

export default function AdminBuilderSearch() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [superintendents, setSuperintendents] = useState<Superintendent[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onSearch = async () => {
    setError(null);
    const filled = [name.trim() !== "", email.trim() !== "", employeeNumber.trim() !== ""].filter(Boolean).length;
    if (filled !== 1) {
      setError("Please provide exactly one of Name, Email, or Employee Number.");
      return;
    }

    const searchBy = email ? "email" : employeeNumber ? "number" : "name";
    const searchTerm = email || employeeNumber || name;

    setLoading(true);
    try {
      const { data } = await httpClient.post("/api/cba/builders/search-filters", {
        searchTerm,
        searchBy,
      });
      setSuperintendents(data.superintendents ?? []);
      setEmployees(data.employees ?? []);
    } catch (e: any) {
      setError(e?.response?.data ?? "Unexpected error while searching");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setName("");
    setEmail("");
    setEmployeeNumber("");
    setSuperintendents([]);
    setEmployees([]);
    setError(null);
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold mb-4">Builder Search</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@highlandhomes.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Employee Number</label>
          <input
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={employeeNumber}
            onChange={(e) => setEmployeeNumber(e.target.value)}
            placeholder="12345"
          />
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={onSearch}
          disabled={loading}
          className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-500 disabled:opacity-50"
        >
          {loading ? "Searching..." : "Search"}
        </button>
        <button
          onClick={reset}
          className="rounded-md bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300"
        >
          Reset
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-semibold mb-2">Superintendents</h2>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Builder Ids</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Builder Numbers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {superintendents.map((s, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{s.name}</td>
                    <td className="px-4 py-2 text-sm text-primary-700">{s.emailAddress}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{s.builderId}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{s.builderNumber}</td>
                  </tr>
                ))}
                {superintendents.length === 0 && !loading && (
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-500" colSpan={4}>No results</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-2">Employees</h2>
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee Number</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {employees.map((e, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{e.employeeFullName}</td>
                    <td className="px-4 py-2 text-sm text-primary-700">{e.emailAddress}</td>
                    <td className="px-4 py-2 text-sm text-gray-700">{e.employeeNumber}</td>
                  </tr>
                ))}
                {employees.length === 0 && !loading && (
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-500" colSpan={3}>No results</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


